/* istanbul ignore file */
import React, { useEffect } from 'react';
import { batch, connect, ConnectedProps } from 'react-redux';
import { getFeatureFlags } from '@noths/polaris-client-feature-flags';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import * as recommendations from '@noths/polaris-client-recommendations';
import { Breakpoint, grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { Media } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
  getUserAttributes,
} from '@noths/polaris-client-user-configuration';
import { addRUMTiming } from '@noths/polaris-client-utils';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { parse } from 'url';

import { ListingsApiErrorGuardContainer } from 'src/components/molecules/ListingsApiErrorGuard';
import { SearchHeader } from 'src/components/molecules/SearchHeader';
import { FilterContainer } from 'src/components/organisms/Filter';
import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { QuickFiltersContainer } from 'src/components/organisms/Filter/containers/QuickFiltersContainer';
import { SlimFilterMenuContainer } from 'src/components/organisms/FilterMenu';
import { NoSearchResultsGuardContainer } from 'src/components/organisms/NoSearchResultsGuard';
import { ProductPagesContainer } from 'src/components/organisms/ProductPage';
import { DELIVERY_QUICK_FILTERS_TITLE } from 'src/constants/copy';
import { Placement } from 'src/constants/recommendations';
import { useFilterNavType } from 'src/hooks/useFilterNavType';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation';
import { ProductsState, requestProducts } from 'src/redux/products';
import { similarProductsRequestComplete } from 'src/redux/richRelevance/actions';
import { requestSimilarProducts } from 'src/redux/richRelevance/thunks';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { AppThunkDispatch, getStore, useStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack';
import * as redirection from 'src/services/redirection';
import * as styles from 'src/styles/productList.styles';
import { NextConfig, PageProps, PageType } from 'src/types';
import * as elementIds from '../constants/elementIds';
import { getProductCodesFromPlacementItem } from '../utils/recommendations';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const mapStateToProps = (state: ReduxApplicationState) => ({
  products: state.products,
  pageLiveRegionText: state.accessibility.statusMessage,
});

const connector = connect(mapStateToProps);

type SearchProductsPageProps = ProductsState & PageProps & ConnectedProps<typeof connector>;

const SearchResultsProductsPage = ({
  products: { searchTerm, items },
  updatedReduxState,
  functionalCookiesConsented,
  pageLiveRegionText,
}: SearchProductsPageProps) => {
  const { dispatch } = useStore();

  useFilterNavType((navType) => dispatch(navigationActions.setInitialPageLoad(navType)));

  useEffect(() => {
    getUserAttributes().then(async (attributes) => {
      recommendations.initRecommendations({
        apiKey: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
        env: process.env.NODE_ENV,
        hasDisabledCookies: !functionalCookiesConsented,
        sessionId: updatedReduxState?.userConfiguration?.richRelevanceSessionId,
        userId: attributes?.sub,
      });

      recommendations.setSearchResultsPage(searchTerm);

      if (items[0]) {
        const productCodes = getProductCodesFromPlacementItem(items[0]);

        recommendations.setSearchResultsProductCodes(productCodes);
      }

      const placementData = await recommendations.getRecommendations([
        Placement.SearchListingsRecentlyViewed,
      ]);

      const similarProductRequests = placementData.map((placement) =>
        dispatch(requestSimilarProducts(placement)),
      );

      await Promise.allSettled(similarProductRequests);
      dispatch(similarProductsRequestComplete());
      addRUMTiming('recommendationsLoaded');
    });
  });

  return (
    <>
      <Head>
        <title>Search results for {searchTerm} | notonthehighstreet.com</title>
        <meta name="robots" content="noindex" />
      </Head>
      <ListingsApiErrorGuardContainer>
        <NoSearchResultsGuardContainer>
          <div css={visuallyHidden} role="status">
            {pageLiveRegionText}
          </div>
          <div css={styles.wrapper}>
            <div css={maxWidthContainer}>
              <SearchHeader searchTerm={searchTerm} />
            </div>
            <div id={elementIds.PRODUCT_CONTAINER_WRAPPER}>
              <SlimFilterMenuContainer />
              <Media lessThan={Breakpoint.L}>
                <div css={styles.quickFiltersWrapper}>
                  <QuickFiltersContainer
                    id={elementIds.DELIVERY_QUICK_FILTERS_NAME}
                    title={DELIVERY_QUICK_FILTERS_TITLE}
                    filterUid={FilterUid.Delivery}
                  />
                </div>
              </Media>
              <div css={[grid, maxWidthContainer, styles.productListWrapper]}>
                <div css={styles.productListSidebar}>
                  <FilterContainer />
                </div>
                <ProductPagesContainer />
              </div>
            </div>
          </div>
        </NoSearchResultsGuardContainer>
      </ListingsApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, res, req }) => {
  const { term, ...queryParams } = query;

  if (term) {
    const pathname = parse(req.url!).pathname!;
    const { dispatch, getState } = getStore({
      router: { pathname, query: queryParams },
    });
    const { currency, delivery_zone, cognitoId, Loop54User, rr_session } = req.cookies;
    const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
    const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
    const featureFlags = (req && getFeatureFlags(req)) || {};
    const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
      parseLocaleSetCookieHeaders(res.getHeaders());

    batch(() => {
      dispatch(navigationActions.setPageType(PageType.Search));
      dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
      dispatch(userConfigurationActions.setCurrencyCode(initialCurrency ?? currency));
      dispatch(userConfigurationActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
      dispatch(userConfigurationActions.setCognitoId(cognitoId));
      dispatch(userConfigurationActions.setFeatureFlags(featureFlags));
      dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));

      if (functionalCookiesConsented) {
        const loop54UserId = getOrSetLoop54UserId(res, Loop54User);
        dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
      }
    });

    const urlPath = req.url;
    const [, [navData, topBannerData]] = await Promise.all([
      (dispatch as AppThunkDispatch)(
        requestProducts({
          isClientRequest: false,
          query,
          userAgent: req.headers['user-agent'],
        }),
      ),
      fetchGlobalContent({ featureFlags, urlPath }),
    ]);

    const state = getState();
    const { products } = state;

    if (products.redirectUrl) {
      return {
        redirect: {
          destination: products.redirectUrl,
          statusCode: StatusCode.MovedPermanently,
        },
      };
    }

    const redirect = redirection.productGuard(products);

    if (redirect) {
      return redirect;
    }

    if (products.error) {
      res.statusCode = StatusCode.InternalServerError;
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);

      const props: PageProps = {
        functionalCookiesConsented,
        updatedReduxState: {
          // Cherry pick state to be updated on the client
          products,
          navigation: { pageType: PageType.Search },
        },
      };

      return {
        props,
      };
    }

    const props: PageProps = {
      featureFlags,
      navData,
      functionalCookiesConsented,
      topBannerData,
      ...selectPagePropsForClientHydration(state),
    };

    return {
      props,
    };
  } else {
    /*
      Commented out as part of interim solution for https://notonthehighstreet.atlassian.net/browse/FFA-961,
      to trigger redirect when empty search term is provided.
      Uncomment when appropriate solution is being implemented.
    */
    /* const props: PageProps = {
      updatedReduxState: {
        products: {
          error: {
            message: 'An unknown error occurred',
          },
        },
      },
    };

    return {
      props,
    }; */

    return {
      redirect: {
        destination: publicRuntimeConfig.hosts.search,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }
};

export default connector(SearchResultsProductsPage);
